import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, ChevronDown, Percent, Tag, CheckCircle2, Sparkles, QrCode } from 'lucide-react';
import { RewardRule, UserRewardDoc } from '../hooks/useExperienceReward';

interface ExpandableRewardCardProps {
  rewardRule: RewardRule;
  isClaimed?: boolean;
  userRewardDoc?: UserRewardDoc | null;
  onViewReward?: (reward: any) => void;
}

export default function ExpandableRewardCard({
  rewardRule,
  isClaimed = false,
  userRewardDoc = null,
  onViewReward,
}: ExpandableRewardCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => setIsExpanded(prev => !prev);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleExpand();
    }
  };

  const discount = rewardRule.discountPercentage || 20;
  const targetName = rewardRule.targetName || 'All Items';

  return (
    <motion.div
      layout
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      aria-label={`Reward: ${rewardRule.title || 'Tour Reward'}. ${isClaimed ? 'Unlocked' : 'Locked'}. Tap to toggle details.`}
      onKeyDown={handleKeyDown}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      className={`my-4 rounded-2xl border backdrop-blur-xl overflow-hidden cursor-pointer transition-colors focus-ring ${
        isClaimed
          ? 'bg-emerald-500/10 border-emerald-500/30 hover:border-emerald-400/50 shadow-[0_4px_20px_rgba(16,185,129,0.15)]'
          : 'bg-amber-500/10 border-amber-500/30 hover:border-amber-400/50 shadow-[0_4px_20px_rgba(250,163,27,0.15)]'
      }`}
      onClick={toggleExpand}
    >
      {/* ── Collapsed Header Row ────────────────────────────────────── */}
      <div className="px-4 py-3.5 flex items-center justify-between gap-3 select-none">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div
            className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
              isClaimed ? 'bg-emerald-400/20 text-emerald-400' : 'bg-primary/20 text-primary'
            }`}
          >
            {isClaimed ? <CheckCircle2 size={18} /> : <Gift size={18} />}
          </div>

          <div className="min-w-0 flex-1 space-y-1">
            {/* 1. Satır: Tam yayılan Badge */}
            <div className="flex items-center">
              <span
                className={`inline-block w-full text-xs font-black uppercase tracking-wider px-2.5 py-1 rounded-md text-left ${
                  isClaimed
                    ? 'bg-emerald-400/20 text-emerald-400 border border-emerald-400/30'
                    : 'bg-primary/20 text-primary border border-primary/30'
                }`}
              >
                {isClaimed ? '✓ Reward Unlocked' : 'Exclusive Reward'}
              </span>
            </div>

            {/* 2. Satır: Reward Başlığı */}
            <p className="text-sm font-bold text-white truncate pt-0.5">
              {rewardRule.title || 'Tour Reward'}
            </p>

            {/* 3. Satır: Tap for details */}
            <p className="text-xs font-medium text-white/70">
              Tap for details
            </p>
          </div>
        </div>

        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className={`shrink-0 p-1.5 rounded-lg ${
            isClaimed ? 'text-emerald-400' : 'text-primary'
          }`}
        >
          <ChevronDown size={18} />
        </motion.div>
      </div>

      {/* ── Expanded Content Area ────────────────────────────────────── */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="border-t border-white/10 px-4 py-3.5 space-y-3 cursor-default"
            onClick={e => e.stopPropagation()}
          >
            {/* Description */}
            <p className="text-xs text-white/90 leading-relaxed">
              {rewardRule.description ||
                'Complete the tour experience to unlock this exclusive reward at the museum store.'}
            </p>

            {/* Badges Row */}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <div
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                  isClaimed
                    ? 'bg-emerald-400/20 text-emerald-400 border border-emerald-400/30'
                    : 'bg-primary/20 text-primary border border-primary/30'
                }`}
              >
                <Percent size={12} />
                <span>{discount}% Discount</span>
              </div>

              <div className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-white/10 text-white/90 border border-white/15">
                <Tag size={12} />
                <span>{targetName}</span>
              </div>

              <div className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-white/10 text-white/90 border border-white/15">
                <Sparkles size={12} />
                <span>Tour Completion</span>
              </div>
            </div>

            {/* Action Button (if claimed) or Incentive Text */}
            {isClaimed ? (
              <div className="pt-2">
                <button
                  onClick={() => onViewReward?.(userRewardDoc || rewardRule)}
                  className="w-full min-h-[44px] py-2.5 bg-emerald-500 text-[#13213b] font-bold text-xs rounded-xl flex items-center justify-center gap-2 shadow-[0_4px_15px_rgba(16,185,129,0.3)] active:scale-[0.98] transition-all uppercase tracking-wider focus-ring"
                >
                  <QrCode size={16} />
                  <span>View Redemption Code / QR</span>
                </button>
              </div>
            ) : (
              <div className="pt-1 flex items-center gap-2 text-xs text-amber-300 font-medium">
                <Sparkles size={14} className="shrink-0 text-amber-400" />
                <span>Finish all checkpoints in this experience to claim your reward!</span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
